import { useQuery, useReactiveVar } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useEffect } from "react";

import {
  EOrganizationType,
  EReconfigurationError,
  EUserManagementGenericError,
  IPropertyIdVariable,
  IShowResidentsData,
} from "../../../interfaces/interfaces";
import {
  organizationIdNameVar,
  reconfigurationErrorVar,
  selectedUserToShowVar,
  userManagementGenericErrorVar,
} from "../../apollo/LocalState";
import { GET_RESIDENT_INFO } from "../../../api/gqlQueries";
import RedAlertMessage from "../../common/RedAlertMessage";
import { SelectedTableEntry } from "../../tables/UsersTables";
import Spinner from "../../Spinner";
import { removeResident } from "../../../actions";
import { renderTenantModalGenericError } from "../common/tenant_modal_common_views";
import { renderUnitDetailsModalContent } from "../../../utils/utils";

export interface IRemoveRegisteredMember {
  decreaseCurrentStep: () => void;
  closeRemoveTenantModal: () => void;
  propertyId: string;
  homeId: string;
  regionLink: string;
}

const RemoveRegisteredMember: React.FC<IRemoveRegisteredMember> = ({
  decreaseCurrentStep,
  closeRemoveTenantModal,
  propertyId,
  homeId,
  regionLink,
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      reconfigurationErrorVar(EReconfigurationError.noError);
    };
  }, []);
  const { organizationId } = useReactiveVar(organizationIdNameVar);
  const selectedUserToShow = useReactiveVar(selectedUserToShowVar);
  const genericError = useReactiveVar(userManagementGenericErrorVar);
  const { loading, data } = useQuery<IShowResidentsData, IPropertyIdVariable>(
    GET_RESIDENT_INFO,
    {
      variables: { propertyId },
    }
  );
  if (loading) {
    return <Spinner />;
  }
  if (!data) {
    throw Error("No data was returned");
  }
  if (!selectedUserToShow) {
    return null;
  }
  const isPossibleAdminUser = "isAdmin" in selectedUserToShow;
  const isLastResident = data.home.residents.length === 1;
  const adminUsersCount = data.home.residents.filter(
    (resident) => resident.isAdmin
  ).length;
  const isLastAdmin = adminUsersCount === 1;
  return (
    <div className="tenants-show-body">
      <div
        className="tenants-show-description-container"
        data-testid="remove-tenant-step-one-container"
      >
        {renderUnitDetailsModalContent({
          descriptionList: [
            "The Home Member will lose access to the home and its devices.",
            "The Home Member will be notified that they have been removed by email.",
          ],
          title: "If you remove the following Home Member:",
        })}
      </div>
      <div className="tenants-show-description-body">
        <SelectedTableEntry />
      </div>
      {isPossibleAdminUser && selectedUserToShow.isAdmin && isLastAdmin && (
        <RedAlertMessage>
          {renderUnitDetailsModalContent({
            centerAlignHeaderForSmallScreen: true,
            descriptionList: [
              "To regain access to this home in the future, you will need to contact a member of this home.",
            ],
            title:
              "Removing ProView Admin will remove this Home from this organization.",
          })}
        </RedAlertMessage>
      )}
      {genericError !== EUserManagementGenericError.noError &&
        renderTenantModalGenericError({ genericError })}
      <div className="invite-tenant-modal-actions margin-top-small">
        <button
          type="button"
          className="tertiary-btn-small tertiary-btn-small-alert"
          onClick={() => {
            decreaseCurrentStep();
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="remove-registered-tenant-btn"
          className="tertiary-btn-small tertiary-btn-small-main tertiary-btn-small-main-alert invite-tenant-modal-actions-submit"
          onClick={() => {
            if (isLastResident) {
              userManagementGenericErrorVar(
                EUserManagementGenericError.lastProViewResident
              );
            } else if (selectedUserToShow) {
              dispatch(
                removeResident({
                  closeRemoveTenantModal,
                  homeId,
                  isAdmin: isPossibleAdminUser && selectedUserToShow.isAdmin,
                  isLastAdmin,
                  organizationId,
                  organizationType: EOrganizationType.PRO_VIEW_ORGANIZATION,
                  propertyId,
                  regionLink,
                  userId: selectedUserToShow.id,
                })
              );
            }
          }}
        >
          Confirm Removal
        </button>
      </div>
    </div>
  );
};

export default RemoveRegisteredMember;
